import { BreakpointObserver } from '@angular/cdk/layout';
import { AsyncPipe, CommonModule, NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { ActivatedRoute } from '@angular/router';
import { HeaderConfigService, OnDestroyBase, TrackingService } from '@os-components';
import { SewIcon, SewWsContentWrapper } from '@sew/ui-components-angular/standalone';
import { Observable } from 'rxjs';
import { filter, mergeMap, takeUntil } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { FormComponent } from '../../components/form/form.component';
import { FsAddPageComponent } from '../../components/fs/fs-add-page/fs-add-page.component';
import { FsBlogLayoutComponent } from '../../components/fs/fs-blog-layout/fs-blog-layout.component';
import { FsService } from '../../components/fs/fs.service';
import { HeroPictureComponent } from '../../components/hero-picture/hero-picture.component';
import { ModalComponent } from '../../components/modal/modal.component';
import { ModalService } from '../../components/modal/modal.service';
import { PostComponent } from '../../components/post/post.component';
import { TileHeaderComponent } from '../../components/tile-header/tile-header.component';
import { CustomBreakpoint, getCustomBreakpoint, mediaQueries } from '../../helpers/breakpoint.helper';
import { tracking } from '../../helpers/tracking';
import { BlogPost } from '../../models/blog-posts.model';
import { BlogLabels } from '../../models/label.model';
import { BlogService } from '../../services/blog/blog.service';
import { ContentService } from '../../services/content/content.service';
import { TagService } from '../../services/tag/tag.service';
import { RouterService } from '../../shared/services/router.service';
import { SearchComponent } from './components/search/search.component';
import { SearchService } from './components/search/search.service';
import { TagArticlesComponent } from './components/tag-articles/tag-articles.component';
import { ScrollService } from './services/scroll.service';

@Component({
    selector: 'blog-landing-page',
    templateUrl: './landing-page.component.html',
    styleUrls: ['./landing-page.component.scss'],
    imports: [
        CommonModule,
        HeroPictureComponent,
        FsAddPageComponent,
        FsBlogLayoutComponent,
        PostComponent,
        SearchComponent,
        ModalComponent,
        FormComponent,
        NgClass,
        TileHeaderComponent,
        TagArticlesComponent,
        AsyncPipe,
        SewWsContentWrapper,
        SewIcon,
    ],
})
export class LandingPageComponent extends OnDestroyBase implements OnInit, OnDestroy, AfterViewInit {
    /**
     * hero post on top of page
     */
    public heroPost?: BlogPost;
    /**
     * first static post
     */
    public firstPost?: BlogPost;
    /**
     * second static post
     */
    public secondPost?: BlogPost;
    /**
     * third static post
     */
    public thirdPost?: BlogPost;

    public blogPosts: BlogPost[] = [];
    public boostedBlogPosts: BlogPost[] = [];

    /**
     * selected tags for filtering Blog Posts with same tag
     */
    public filteredTags: string[] = [];
    /**
     * Array of Blog Posts filterd by tags
     */
    public filteredPosts: BlogPost[] = [];

    /**
     * search query
     */
    public searchQuery = '';

    /**
     * Result Array of Blog posts from search
     */
    public searchResults: BlogPost[] = [];

    /**
     * number of blog posts in repeating pattern of the blog content
     */
    public repeatingBlogPostsCount = 6;

    public arrIndex: number[] = [];

    /**
     * current breakpoint of the application. Given by the breakpoint service from sew-web-features package
     */
    public breakpoint: CustomBreakpoint = 0;

    /**
     * preview id for hero posts and boosted posts
     */
    public layoutPreviewId = '';

    /**
     * determines if the show more posts on the bottom of the page is shown or not.
     */
    public showLoadMore = true;

    public env = environment;

    public CustomBreakpoint = CustomBreakpoint;
    public showForm = true;
    public labels?: Observable<BlogLabels>;

    public constructor(
        private contentService: ContentService,
        private routerService: RouterService,
        private tagService: TagService,
        private searchService: SearchService,
        public breakpointObserver: BreakpointObserver,
        private activatedRoute: ActivatedRoute,
        private scrollService: ScrollService,
        private modalService: ModalService,
        private blogService: BlogService,
        private titleService: Title,
        private metaService: Meta,
        private fsService: FsService,
        private changeDetection: ChangeDetectorRef,
        private configService: HeaderConfigService,
        private trackingService: TrackingService
    ) {
        super();
    }

    public ngOnInit(): void {
        this.setDefaultTitle();
        this.contentService.metaData.pipe(takeUntil(this.destroySubject)).subscribe((metaData) => {
            this.titleService.setTitle(metaData.pageTitle);
            this.metaService.updateTag({
                name: 'description',
                content: metaData.pageDescription,
            });
        });

        this.activatedRoute.queryParams.pipe(takeUntil(this.destroySubject)).subscribe((params) => {
            const tag = params.tags;
            if (tag) {
                this.tagService.addTag(tag);
                this.tagService.clearBrowserUrl();
            }
            const search = params.search;
            if (search && !this.searchService.getSearchQuery()) {
                setTimeout(() => {
                    window.scrollTo(0, 1060);
                }, 500);
                this.searchService.setSearchQuery(search);
                this.searchService.clearBrowserUrl();
            }
        });

        this.labels = this.blogService.labelsUpdated;

        this.initBlogPosts();

        this.initTags();
        this.initSearch();

        if (this.env.preview) {
            this.fsService.clearPreviewElement();

            this.fsService.rerenderView
                .pipe(
                    takeUntil(this.destroySubject),
                    mergeMap(() => this.configService.language),
                    filter((language) => language.length > 0)
                )
                .subscribe(() => {
                    // the rerenderView event comes possibly before the data is written into caas
                    // therefore we wait 350ms, before reloading the data
                    setTimeout(() => {
                        this.contentService.initBlogPosts();
                        this.initBlogPosts();
                    }, 350);
                });
        }

        this.initBreakpointListener();
    }

    public ngAfterViewInit(): void {
        if (this.scrollService.getPageYOffset()) {
            setTimeout(() => {
                window.scrollTo(0, this.scrollService.getPageYOffset());
            }, 500);
        }
    }

    public ngOnDestroy(): void {
        super.ngOnDestroy();
        // store scroll bar position for restoration of page stage
        this.scrollService.setPageYOffset(window.pageYOffset);
    }

    /**
     * Click on Post
     *
     * @param post Blog Post
     */
    public postClicked(post: BlogPost): void {
        this.trackingService.trackEvent(tracking.step.article.clicked);
        this.routerService.navigateArticle(post);
    }

    public searchResultClicked(): void {
        this.searchService.clearSearchResults();
    }

    public onLoadArticles(): void {
        this.contentService.getNextIteration();
    }

    public tagClicked(tag: string): void {
        this.trackingService.trackEvent(tracking.step.tag.clicked);
        this.tagService.addTag(tag);
    }

    public filteredTagClicked(filteredTag: string): void {
        this.tagService.removeTag(filteredTag);
    }

    public onCloseSearch(): void {
        this.searchService.clearSearchResults();
    }

    public openSubscribeDialog(id: string): void {
        this.trackingService.trackEvent(tracking.step.subscribe.landingSubscribeDialog.open);
        this.modalService.open(id);
    }

    public closeSubscribeDialog(id: string): void {
        this.trackingService.trackEvent(tracking.step.subscribe.landingSubscribeDialog.close);
        this.modalService.close(id);
    }

    public getTeaserClass(i: number): string {
        const rest = i % 5;

        if (rest < 2) {
            return 'no-padding span-3';
        } else {
            return 'no-padding span-2';
        }
    }

    private setDefaultTitle() {
        this.titleService.setTitle('Blog | SEW-EURODRIVE');
    }

    private initBlogPosts(): void {
        this.contentService.initializedContent.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.getDataFromContentService();
        });

        this.contentService.updatedContent.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            this.blogPosts = this.contentService.getBlogPosts();
            this.calcIterationArr();
        });

        this.contentService.reachedLastPage.pipe(takeUntil(this.destroySubject)).subscribe((value) => {
            this.showLoadMore = !value;
        });

        this.getDataFromContentService();
    }

    private initTags(): void {
        this.tagService
            .getTags()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((tags) => {
                if (this.searchService.getSearchQuery().length && tags.length) {
                    this.searchService.clearSearchResults();
                }
                this.filteredTags = tags;
            });

        this.tagService
            .getFilteredPosts()
            .pipe(takeUntil(this.destroySubject))
            .subscribe((posts) => (this.filteredPosts = posts));
    }

    private initSearch(): void {
        if (this.searchService.getSearchQuery()) {
            this.searchResults = this.searchService.getSearchResults();
            this.searchQuery = this.searchService.getSearchQuery();
        }
        this.searchService.updatedSearchResults.pipe(takeUntil(this.destroySubject)).subscribe(() => {
            if (this.filteredTags.length && this.searchService.getSearchQuery().length) {
                this.tagService.clearAllTags();
            }
            this.searchQuery = this.searchService.getSearchQuery();
            this.searchResults = this.searchService.getSearchResults();
        });
    }

    private calcIterationArr(): void {
        this.arrIndex = [];
        const iterations = Math.ceil(this.blogPosts.length / this.repeatingBlogPostsCount);
        for (let i = 0; i < iterations; i++) {
            this.arrIndex.push(i * this.repeatingBlogPostsCount);
        }
    }

    private getDataFromContentService(): void {
        this.heroPost = this.contentService.getHeroPost();
        this.firstPost = this.contentService.getFirstPost();
        this.secondPost = this.contentService.getSecondPost();
        this.thirdPost = this.contentService.getThirdPost();
        this.blogPosts = this.contentService.getBlogPosts();
        this.boostedBlogPosts = this.contentService.getBoostedPosts();

        this.layoutPreviewId = this.contentService.getLayoutPreviewId();

        this.calcIterationArr();
        this.changeDetection.detectChanges();
    }

    private initBreakpointListener(): void {
        this.breakpointObserver
            .observe([mediaQueries.mq1, mediaQueries.mq2, mediaQueries.mq3])
            .pipe(takeUntil(this.destroySubject))
            .subscribe((state) => {
                this.breakpoint = getCustomBreakpoint(state);
            });
    }
}
